import { useAuth } from '@clerk/nextjs';
import { useMutation } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

export interface ConversionCheckoutParams {
  /** Stripe price the organisation converts onto. Interval travels with it; currency must be the trial's. */
  priceId: string;
  /** Seats for the new subscription. */
  userCount: number;
  isAnnual: boolean;
  isCouponApplied: boolean;
  billingCountry: BillingCountry;
  preferredBillingCountry: AvailableCountries;
}

export interface ConversionCheckoutResponse {
  success: true;
  /** Stripe-hosted, subscription-mode Checkout URL. */
  url: string;
  returnUrl: string;
}

export type ConversionCheckoutErrorCode =
  | 'not-billing-capable'
  | 'already-subscribed'
  | 'currency-mismatch'
  | 'unknown';

export interface ConversionCheckoutErrorResponse {
  success: false;
  error: string;
  code: ConversionCheckoutErrorCode;
}

/**
 * Opens subscription-mode Checkout for an organisation converting its no-card trial, trialing or
 * paused. Same route and body as a paid signup (`POST /checkout`); the server recognises the trial
 * and tags the session so the checkout-completed webhook retires it once the new subscription is
 * paid for. A mutation, not a refetch: refetch resolves on failure and would push ''.
 */
const useConversionCheckout = () => {
  const { orgId } = useAuth();

  return useMutation<ConversionCheckoutResponse, AxiosError<ConversionCheckoutErrorResponse>, ConversionCheckoutParams>(
    {
      mutationFn: async (params) => {
        const response = await axios.post<ConversionCheckoutResponse>(
          `${process.env.NEXT_PUBLIC_API_APP_ENDPOINT}/checkout`,
          {
            price: params.priceId,
            orgId,
            isTrialMode: false,
            userCount: params.userCount,
            isAnnual: params.isAnnual,
            isCouponApplied: params.isCouponApplied,
            billingCountry: params.billingCountry,
            preferredBillingCountry: params.preferredBillingCountry,
          },
        );
        return response.data;
      },
    },
  );
};

export { useConversionCheckout };
